// 検証ケースの「出どころ」と「リリース前のチェックに入れるか」。
// 以前は「ケースの種類」（case_set）の1つのセレクトで、出どころ（合成か本番か）・作り方（流れの一部か）・
// チェックの範囲（毎回確かめるか）・難しさ（分かれ目か）を混ぜて選ばせていた。
// 出どころは作り方から決まるので表示するだけにし、人に聞くのは「リリース前のチェックに入れるか」だけにする。
// case_set の値と意味は変えない：チェックのオン＝core。外したときの箱はサーバーが決める
// （SetDunningEvaluationCasesRegressionCommand のコメントが一次情報）
import type { CaseSet, EvaluationCase } from "./types"

/** 出どころの種類。standard＝標準ケース、production_draft＝本番の下書き、dunning_case＝督促ケース、manual＝手で作った */
export type CaseOriginKind = "standard" | "production_draft" | "dunning_case" | "manual"

export const CASE_ORIGIN_LABELS: Record<CaseOriginKind, string> = {
  standard: "標準ケース",
  production_draft: "本番の下書きから",
  dunning_case: "督促ケースから",
  manual: "手で作った",
}

export const CASE_ORIGIN_KINDS = Object.keys(CASE_ORIGIN_LABELS) as CaseOriginKind[]

/** リリース前のチェックに入れる箱（プロンプトの比較の既定の対象・保存時の自動評価の対象） */
export const RELEASE_CHECK_CASE_SET: CaseSet = "core"

/** 本番の生成リクエストから読み込んだときの説明（API の読み込みが先頭に付ける）。生成した日を取り出す */
const PRODUCTION_DRAFT = /本番の生成#\d+（(\d{4})-(\d{2})-(\d{2})/
/** Dev の取り込み（rake）で作った、本番の下書きからのケースのタイトル */
const PRODUCTION_DRAFT_TITLE = /^\[実データ取込\]/

const monthDay = (month: string, day: string) => `${Number(month)}月${Number(day)}日`

/** 日本時間の「9月19日 12:00」 */
const jstMonthDayTime = (iso: string) => {
  const time = Date.parse(iso)
  if (Number.isNaN(time)) return iso
  const jst = new Date(time + 9 * 60 * 60 * 1000).toISOString()
  return `${monthDay(jst.slice(5, 7), jst.slice(8, 10))} ${jst.slice(11, 16)}`
}

export type CaseOrigin = { kind: CaseOriginKind; label: string }

/**
 * ケースの出どころ。標準ケースかどうか・取り込み元の督促ケース・説明（本番の生成から読み込んだか）で決める。
 * 本番の下書きからかどうかは、今は説明とタイトルにしか残らない（生成リクエスト ID は検証ケースに持たせていない）
 */
export function caseOrigin(record: EvaluationCase): CaseOrigin {
  if (record.standard) return { kind: "standard", label: "標準ケース" }
  const draft = PRODUCTION_DRAFT.exec(record.description ?? "")
  if (draft)
    return {
      kind: "production_draft",
      label: `本番の下書き（${monthDay(draft[2], draft[3])}生成）から`,
    }
  if (PRODUCTION_DRAFT_TITLE.test(record.title))
    return { kind: "production_draft", label: "本番の下書きから" }
  if (record.sourceDunningCaseId)
    return {
      kind: "dunning_case",
      label: `督促ケース #${record.sourceDunningCaseId} の ${jstMonthDayTime(record.scenario.asOf)} 時点から`,
    }
  return {
    kind: "manual",
    label: record.parentCaseId ? "手で作った（流れの続きの時点）" : "手で作った",
  }
}

/** リリース前のチェックに入っているか */
export const inReleaseCheck = (record: Pick<EvaluationCase, "caseSet">) =>
  record.caseSet === RELEASE_CHECK_CASE_SET
